import { GAME_2048_TARGETS } from './game2048';
import { parse2048Session, type Game2048Session } from './game2048Session';
import { supabase, type DatabaseScope } from './supabase';

export const is2048RecordTile = (value: unknown): value is number => (
  typeof value === 'number' && GAME_2048_TARGETS.some(target => target > 4096 && target === value)
);

export const read2048Record = async (database = supabase): Promise<number | null> => {
  const { data, error } = await database.from('game_2048_records').select('max_tile').limit(1);
  if (error) {
    throw error;
  }
  if (!Array.isArray(data) || data.length > 1 || (data.length === 1 && !is2048RecordTile(data[0]?.max_tile))) {
    throw new TypeError('Invalid 2048 record response');
  }
  return data.length === 0 ? null : data[0].max_tile;
};

export const save2048Record = async (
  session: Game2048Session,
  scope: DatabaseScope
): Promise<{ maxTile: number; improved: boolean }> => {
  parse2048Session(JSON.stringify(session));
  if (session.principal !== scope.principal || session.outcome === 'active' || !is2048RecordTile(session.highestTile)) {
    throw new Error('Invalid 2048 record submission');
  }
  const { data, error } = await scope.client.rpc('save_2048_record', {
    achieved_tile: session.highestTile,
    achieved_run_id: session.runId,
    achieved_puzzle_date: session.identity.date,
    achieved_score: session.engine.score,
    achieved_moves: session.engine.moves,
    achieved_elapsed_time_seconds: session.elapsedTime,
    achieved_undos_used: session.undosUsed,
    achieved_outcome: session.outcome
  });
  if (error) {
    throw error;
  }
  if (
    !Array.isArray(data) || data.length !== 1 ||
    !is2048RecordTile(data[0]?.max_tile) || typeof data[0]?.improved !== 'boolean' ||
    data[0].max_tile < session.highestTile || (data[0].improved && data[0].max_tile !== session.highestTile)
  ) {
    throw new TypeError('Invalid saved 2048 record response');
  }
  return { maxTile: data[0].max_tile, improved: data[0].improved };
};
